import Tooltip from "@common/Tooltip";
import React, {useState, useEffect} from 'react';
import {sortArr} from './Utils';
import {GRID_ENUMS} from "./Styles";
import LineChartLegend, {LEGEND_SHAPES} from "./LineChartLegend";

const StudentPerformanceView = props => {
    const {testAssignmentsData, students, studentIds, classIds, initialClassIds, schoolId, averageScoreTypeGrowthSelected, performanceOrderBy,
            selectedAggregateLevel, showAverageScoreLine, contentType, openDialogCallback} = props,
        preTestLabel = 'pre/mid-test',
        postTestLabel = 'post-test',
        [studentData, setStudentData] = useState([]);

    const generateStudentData = (data) => {
        const tempStudentData = [];

        students.forEach((student) => {
            if (studentIds.includes(student.userId)) {
                // The main data object for a student row, with 'pres' array and 'post' object
                const studentRow = {
                    key: String(student.userId), id: student.userId, columnType: GRID_ENUMS.USER, name: `${student.lastName}, ${student.firstName}`,
                    firstName: student.firstName, lastName: student.lastName,
                    pres: {}, // We'll populate this with pre-test data, keyed by assignmentId
                    post: { performance: GRID_ENUMS.NOT_AVAILABLE, average: 0, distanceFromAverage: GRID_ENUMS.NOT_AVAILABLE, skillIds: [], hasResults: false },
                    textbookTopics: [], curriculumTopicGroups: [], curriculumTopics: [],
                    hasResults: false, hasTextbookResults: false, hasCurriculumResults: false
                };

                const studentTextbookTopicMap = {}, studentTextBookConceptMap = {}, studentCurriculumTopicGroupMap = {},
                    studentCurriculumTopicMap = {}, studentCurriculumConceptMap = {}, studentSkillMap = {};

                // 1. Get a master list of all unique skills from all tests for this student.
                const masterSkillList = {};
                data.forEach(test => {
                    const result = test.testResultBeans.find(r => r.userId === student.userId);
                    if (result) {
                        result.testResultSkills.forEach(skill => {
                            if (!masterSkillList[skill.skillId]) {
                                masterSkillList[skill.skillId] = skill;
                            }
                        });
                    }
                });

                // 2. Build the hierarchical structure from the master skill list.
                Object.values(masterSkillList).forEach(skill => {
                    // This block ensures every skill gets an entry in the hierarchy, regardless of which test it came from.
                    if (skill.curriculumTopicGroupId && !studentCurriculumTopicGroupMap[skill.curriculumTopicGroupId]) {
                        studentCurriculumTopicGroupMap[skill.curriculumTopicGroupId] = { id: skill.curriculumTopicGroupId, name: skill.curriculumTopicGroupName, pres: {}, post: { performance: 0, average: 0, skillIds: [], hasResults: false }, curriculumTopics: [] };
                        studentRow.curriculumTopicGroups.push(studentCurriculumTopicGroupMap[skill.curriculumTopicGroupId]);
                    }
                    if (skill.curriculumTopicId && !studentCurriculumTopicMap[skill.curriculumTopicId]) {
                        studentCurriculumTopicMap[skill.curriculumTopicId] = { id: skill.curriculumTopicId, name: skill.curriculumTopicName, pres: {}, post: { performance: 0, average: 0, skillIds: [], hasResults: false }, curriculumConcepts: [] };
                        studentRow.curriculumTopics.push(studentCurriculumTopicMap[skill.curriculumTopicId]);
                    }
                    if (skill.curriculumConceptId && !studentCurriculumConceptMap[skill.curriculumConceptId]) {
                        studentCurriculumConceptMap[skill.curriculumConceptId] = { id: skill.curriculumConceptId, name: skill.curriculumConceptName, pres: {}, post: { performance: 0, average: 0, skillIds: [], hasResults: false }, skills: [] };
                        if (studentCurriculumTopicMap[skill.curriculumTopicId]) studentCurriculumTopicMap[skill.curriculumTopicId].curriculumConcepts.push(studentCurriculumConceptMap[skill.curriculumConceptId]);
                    }
                    if (!studentTextbookTopicMap[skill.textbookTopicId]) {
                        studentTextbookTopicMap[skill.textbookTopicId] = { id: skill.textbookTopicId, name: skill.textbookTopicName, pres: {}, post: { performance: 0, average: 0, skillIds: [], hasResults: false }, concepts: [] };
                        studentRow.textbookTopics.push(studentTextbookTopicMap[skill.textbookTopicId]);
                    }
                    if (!studentTextBookConceptMap[skill.textbookConceptId]) {
                        studentTextBookConceptMap[skill.textbookConceptId] = { id: skill.textbookConceptId, name: skill.textbookConceptName, pres: {}, post: { performance: 0, average: 0, skillIds: [], hasResults: false }, skills: [] };
                        studentTextbookTopicMap[skill.textbookTopicId].concepts.push(studentTextBookConceptMap[skill.textbookConceptId]);
                    }
                    if (!studentSkillMap[skill.skillId]) {
                        studentSkillMap[skill.skillId] = { id: skill.skillId, name: skill.name, pres: {}, post: { performance: 0, average: 0, hasResults: false } };
                        studentTextBookConceptMap[skill.textbookConceptId].skills.push(studentSkillMap[skill.skillId]);
                        if (studentCurriculumConceptMap[skill.curriculumConceptId]) studentCurriculumConceptMap[skill.curriculumConceptId].skills.push(studentSkillMap[skill.skillId]);
                    }
                });

                // 3. Populate the structure with data from each test.
                data.forEach((testAssignmentData) => {
                    const studentTestResult = testAssignmentData.testResultBeans.find(trb => trb.userId === student.userId);
                    if (studentTestResult) {
                        const isPreTest = studentTestResult.testPurpose === 'DIAGNOSTIC_PRE_TEST';
                        const assignmentId = testAssignmentData.testAssignmentId;

                        if (!isPreTest) { // This is the single latest post-test
                            studentRow.post = {
                                performance: studentTestResult.percentCorrect,
                                distanceFromAverage: Math.abs(testAssignmentData.testAveragePercent - studentTestResult.percentCorrect),
                                average: 0, skillIds: [], hasResults: true, assignmentId: assignmentId, name: testAssignmentData.testName || postTestLabel
                            };
                        } else { // This is a pre-test
                            if (!studentRow.pres[assignmentId]) {
                                studentRow.pres[assignmentId] = {
                                    id: assignmentId, name: testAssignmentData.testName || preTestLabel, performance: studentTestResult.percentCorrect,
                                    distanceFromAverage: Math.abs(testAssignmentData.testAveragePercent - studentTestResult.percentCorrect),
                                    average: 0, skillIds: [], hasResults: true, assignmentId: assignmentId
                                };
                            }
                        }

                        studentTestResult.testResultSkills.forEach((skill) => {
                            let averageMarkPercent = 0;
                            if (averageScoreTypeGrowthSelected === 3) {
                                const schoolSkillMapEntry = testAssignmentData.testAssignmentSkillMap[skill.skillId + '~' + schoolId];
                                if (schoolSkillMapEntry) averageMarkPercent = isNaN(schoolSkillMapEntry.markPercent) ? 0 : schoolSkillMapEntry.markPercent;
                            } else if (averageScoreTypeGrowthSelected === 1 && classIds.length !== initialClassIds.length) {
                                classIds.forEach(classId => {
                                    const classSkillMapEntry = testAssignmentData.testAssignmentSkillMap[skill.skillId + '|' + classId];
                                    if (classSkillMapEntry) averageMarkPercent += isNaN(classSkillMapEntry.classAverageMarkPercent) ? 0 : classSkillMapEntry.classAverageMarkPercent;
                                });
                                averageMarkPercent = (averageMarkPercent / classIds.length);
                            } else {
                                const assignmentSkillMapEntry = testAssignmentData.testAssignmentSkillMap[skill.skillId];
                                if (assignmentSkillMapEntry) averageMarkPercent = isNaN(assignmentSkillMapEntry.classAverageMarkPercent) ? 0 : assignmentSkillMapEntry.classAverageMarkPercent;
                            }
                            const skillMarkPercent = isNaN(skill.markPercent) ? 0 : skill.markPercent;

                            const entities = [ studentRow, studentTextbookTopicMap[skill.textbookTopicId], studentTextBookConceptMap[skill.textbookConceptId],
                                studentSkillMap[skill.skillId], studentCurriculumTopicGroupMap[skill.curriculumTopicGroupId], studentCurriculumTopicMap[skill.curriculumTopicId],
                                studentCurriculumConceptMap[skill.curriculumConceptId]
                            ].filter(Boolean);

                            entities.forEach(entity => {
                                if (isPreTest) {
                                    if (!entity.pres[assignmentId]) {
                                        entity.pres[assignmentId] = { id: assignmentId, name: testAssignmentData.testName || preTestLabel, performance: 0, average: 0, skillIds: [], hasResults: false };
                                    }
                                    const preEntry = entity.pres[assignmentId];
                                    preEntry.performance += skillMarkPercent;
                                    preEntry.average += averageMarkPercent;
                                    preEntry.hasResults = true;
                                    if (preEntry.skillIds && !preEntry.skillIds.includes(skill.skillId)) {
                                        preEntry.skillIds.push(skill.skillId);
                                    }
                                } else { // Is the single post-test
                                    entity.post.performance += skillMarkPercent;
                                    entity.post.average += averageMarkPercent;
                                    entity.post.hasResults = true;
                                    if (entity.post.skillIds && !entity.post.skillIds.includes(skill.skillId)) {
                                        entity.post.skillIds.push(skill.skillId);
                                    }
                                }
                            });
                        });
                    }
                });

                const allEntities = [ studentRow, ...Object.values(studentTextbookTopicMap), ...Object.values(studentTextBookConceptMap), ...Object.values(studentSkillMap),
                    ...Object.values(studentCurriculumTopicGroupMap), ...Object.values(studentCurriculumTopicMap), ...Object.values(studentCurriculumConceptMap)
                ];

                allEntities.forEach(entity => {
                    if (!entity.post.skillIds) {
                        entity.post.average = percentCalc(entity.post.average, 1);
                    } else {
                        entity.post.performance = percentCalc(entity.post.performance, entity.post.skillIds.length);
                        entity.post.average = percentCalc(entity.post.average, entity.post.skillIds.length);
                    }

                    Object.values(entity.pres).forEach(pre => {
                        if (!pre.skillIds) {
                            pre.average = percentCalc(pre.average, 1);
                        } else {
                            pre.performance = percentCalc(pre.performance, pre.skillIds.length);
                            pre.average = percentCalc(pre.average, pre.skillIds.length);
                        }
                    });
                    entity.pres = Object.values(entity.pres).sort((a,b) => new Date(a.assignedDate) - new Date(b.assignedDate));
                });


                studentRow.hasResults = studentRow.post.hasResults || studentRow.pres.length > 0;
                studentRow.hasTextbookResults = studentRow.textbookTopics.length > 0;
                studentRow.hasCurriculumResults = studentRow.curriculumTopics.length > 0;

                if (studentRow.hasResults) {
                    tempStudentData.push(studentRow);
                }
            }
        });

        sortArr(tempStudentData, performanceOrderBy.order, performanceOrderBy.direction);
        return tempStudentData;
    }

    const percentCalc = (num, den) => {
        if (num === 0 || den === 0) return 0;
        return Math.round(num / den);
    }

    useEffect(() => {
        if (testAssignmentsData.length > 0) {
            // Get all pre-tests
            const preTests = testAssignmentsData.filter(test => test.testPurpose === 'DIAGNOSTIC_PRE_TEST');

            // Get the single latest post-test
            const latestPostTest = testAssignmentsData
                .filter(test => test.testPurpose === 'DIAGNOSTIC_POST_TEST')
                .reduce((latest, current) => {
                    if (!latest) return current;
                    return new Date(current.assignedDate) > new Date(latest.assignedDate) ? current : latest;
                }, null);

            const filteredTests = [...preTests];
            if (latestPostTest) filteredTests.push(latestPostTest);

            setStudentData(generateStudentData(filteredTests));
        } else {
            setStudentData([]);
        }
    }, [testAssignmentsData, studentIds, performanceOrderBy, classIds, averageScoreTypeGrowthSelected]);

    const getAverageMarker = (averagePercent, pre) => (
        <div className={'average-marker ' + (pre ? 'pre' : 'post')} style={{left: `${averagePercent}%`}}>
            <div><svg width={24} height={30}><mask id={'circleCutAverage'}><circle fill={'white'} cx={"12"} cy={"15"} r={"30"}></circle><circle fill={'black'} cx={"12"} cy={"15"} r={"4"}></circle></mask><polygon mask={'url(#circleCutAverage)'} points="0 15, 12 30, 24 15, 12 0" fill={'#888'} stroke={'#888'} /></svg></div>
            <span className={'label ch-bold'}>{averagePercent}%</span>
        </div>
    );

    const getUserMarker = (markPercent, pre) => (
        <div className={'performance-marker ' + (pre ? 'pre' : 'post')} style={{left: `${markPercent}%`}}>
            <div className={'background'}><span className={'label ch-bold'}>{markPercent}%</span></div>
        </div>
    );

    const renderPerformanceRows = (item, studentId) => {
        const hasPostResults = item.post && item.post.hasResults;
        return (
            <React.Fragment key={item.id}>
                {/* Pre-Test Rows */}
                {item.pres.length > 0 ? item.pres.map((pre, index) => {
                    const hasPreResults = pre.hasResults;
                    return (
                        <tr key={pre.id} className={'performance-skill-row pre'}>
                            {index === 0 && <td rowSpan={Math.max(1, item.pres.length) + 1} className={'skill-name'}><div>{item.name}</div></td>}
                            <td className={'performance-label'}>{pre.name}</td>
                            <td>
                                <div className={`performance-bar ${hasPreResults ? 'results' : 'no-results'}`} onClick={() => {if (!hasPreResults) return; openDialogCallback(studentId, [pre.assignmentId]);}}>
                                    {hasPreResults && showAverageScoreLine && getAverageMarker(pre.average, true)}
                                    {hasPreResults && pre.performance > 0 && getUserMarker(pre.performance, true)}
                                    {hasPreResults && <div className={'pre performance'} style={{width: `${pre.performance}%`}}></div>}
                                </div>
                            </td>
                        </tr>
                    )
                }) : (
                    <tr className={'performance-skill-row pre'}>
                        <td className={'skill-name'} rowSpan={2}><div>{item.name}</div></td>
                        <td className={'performance-label'}>{preTestLabel}</td>
                        <td><div className={'performance-bar no-results'}></div></td>
                    </tr>
                )}
                {/* Post-Test Row */}
                <tr className={'performance-skill-row post'}>
                    <td className={'performance-label'}>{item.post.name || postTestLabel}</td>
                    <td>
                        <div className={`performance-bar ${hasPostResults ? 'results' : 'no-results'}`} onClick={() => {if (!hasPostResults) return; openDialogCallback(studentId, [item.post.assignmentId]);}}>
                            {hasPostResults && showAverageScoreLine && getAverageMarker(item.post.average, false)}
                            {hasPostResults && item.post.performance > 0 && getUserMarker(item.post.performance, false)}
                            {hasPostResults && <div className={'post performance'} style={{width: `${item.post.performance}%`}}></div>}
                        </div>
                    </td>
                </tr>
            </React.Fragment>
        );
    };

    const legendData = [
        { text: 'Student pre/mid-test performance', colors: { background: '#2883EA', border: '#2883EA' }, shape: LEGEND_SHAPES.SQUARE },
        { text: 'Student post-test performance', colors: { background: '#EBE1FE', border: '#EBE1FE' }, shape: LEGEND_SHAPES.SQUARE },
        { text: 'Average', colors: { background: '#888', border: '#888' }, shape: LEGEND_SHAPES.DIAMOND }
    ];

    return (
        <div className="student-performance-view flexColumn">
            <LineChartLegend data={legendData} showAsCard={true} cardColor={'#FAFAFB'} direction={'horizontal'} style={{width: 'auto', justifyContent: 'center', padding: '10px 20px'}}/>
            {studentData.length > 0 ? (
                <table className={'performance-table'}>
                    <tbody>
                    {studentData.map((student, index) => (
                        <React.Fragment key={student.key}>
                            {selectedAggregateLevel === 'syllabus' ? renderPerformanceRows(student, student.id) : (
                                <>
                                    <tr className={'performance-student-row'}><td colSpan={3} className={'performance-student-label'}>{student.name}</td></tr>
                                    { (GRID_ENUMS.CURRICULUM === contentType ? student.hasCurriculumResults : student.hasTextbookResults) ? (
                                        <>
                                            <tr><td colSpan={2} className={'ch-bold'}>Skill</td><td className={'ch-bold'}>Performance</td></tr>

                                            {/* This section contains the logic for grouping by Topic and Concept */}
                                            { (GRID_ENUMS.CURRICULUM === contentType ? student.curriculumTopics : student.textbookTopics).map(topic => {
                                                if (selectedAggregateLevel === 'topic') return renderPerformanceRows(topic, student.id);
                                                return (
                                                    <React.Fragment key={topic.id}>
                                                        <tr className={'performance-chapter-row'}><td colSpan={3} className={'ch-bold'}>{topic.name}</td></tr>
                                                        { (GRID_ENUMS.CURRICULUM === contentType ? topic.curriculumConcepts : topic.concepts).map(concept => {
                                                            if (selectedAggregateLevel === 'concept') return renderPerformanceRows(concept, student.id);
                                                            return (
                                                                <React.Fragment key={concept.id}>
                                                                    <tr className={'performance-concept-row'}><td colSpan={3} className={'ch-bold'}>{concept.name}</td></tr>
                                                                    {concept.skills.map(skill => renderPerformanceRows(skill, student.id))}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <tr><td colSpan={3} className={'ch-dashboard-warning-text'}>There is no {contentType} skill content for this student.</td></tr>
                                    )}
                                </>
                            )}
                            {index < (studentData.length - 1) && <tr className={'performance-divider'}><td colSpan={3}></td></tr>}
                        </React.Fragment>
                    ))}
                    </tbody>
                </table>
            ) : (
                <div className="ch-dashboard-warning-text" style={{padding: '20px'}}>No student data to display for the selected filters.</div>
            )}
        </div>
    );
}

export default StudentPerformanceView;